import React, { useEffect, useState } from 'react';
import apiClient from '../services/apiClient';
import { toast } from 'react-toastify';

interface Gateway {
  provider_type: 'sms' | 'email' | 'social';
  provider_name: string;
  credentials: any;
  is_active: boolean;
}

const GatewayConfigs: React.FC = () => {
  const [gateways, setGateways] = useState<Gateway[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'sms' | 'email' | 'social'>('sms');

  useEffect(() => {
    fetchGateways();
  }, []);

  const fetchGateways = async () => {
    try {
      const response = await apiClient.get('/admin/gateway-configs');
      if (response.data.status) {
        setGateways(response.data.data);
      }
    } catch (error) {
      toast.error("Failed to fetch gateways");
    }
  };

  const updateGateway = async (index: number) => {
    setLoading(true);
    try {
      const gateway = gateways[index];
      const response = await apiClient.post('/admin/gateway-configs', {
        provider_name: gateway.provider_name,
        credentials: gateway.credentials,
        is_active: gateway.is_active
      });

      if (response.data.status) {
        toast.success(`${gateway.provider_name} config updated successfully`);
        // Refresh to get active states correctly synced
        fetchGateways();
      } else {
        toast.error("Failed to update config");
      }
    } catch (error) {
      toast.error("Error updating config");
    } finally {
      setLoading(false);
    }
  };

  const handleCredentialChange = (index: number, key: string, value: string) => {
    const updated = [...gateways];
    updated[index].credentials[key] = value;
    setGateways(updated);
  };

  const toggleActive = (index: number) => {
    const updated = [...gateways];
    updated[index].is_active = !updated[index].is_active;
    setGateways(updated);
  };

  const renderCredentialsInputs = (gateway: Gateway, index: number) => {
    return Object.keys(gateway.credentials).map((key) => (
      <div key={key} className="w-full mb-4">
        <label className="block text-xs font-medium text-gray-400 mb-1 capitalize">
          {key.replace('_', ' ')}
        </label>
        <input
          type="text"
          value={gateway.credentials[key] || ''}
          onChange={(e) => handleCredentialChange(index, key, e.target.value)}
          className="w-full bg-slate-900 border-slate-700/60 shadow-2xl text-white text-sm px-3 py-2 rounded border focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
      </div>
    ));
  };

  const tabs = [
    { id: 'sms', label: 'SMS Gateways', icon: 'fas fa-sms' },
    { id: 'email', label: 'Email Providers', icon: 'fas fa-envelope' },
    { id: 'social', label: 'Social Logins', icon: 'fas fa-share-alt' }
  ];

  return (
    <div className="ml-0 lg:ml-[256px] p-4 sm:p-6 text-white min-h-screen bg-[#151929]">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-xl sm:text-2xl font-bold mb-6 text-white tracking-tight">Gateway Configurations</h2>

        {/* Custom Tabs */}
        <div className="flex flex-nowrap overflow-x-auto space-x-2 border-b border-gray-700 mb-6 pb-2" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`whitespace-nowrap px-4 py-2 text-sm font-bold transition-colors border-b-2 ${
                activeTab === tab.id
                  ? 'border-indigo-500 text-indigo-400'
                  : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-500'
              }`}
            >
              <i className={`${tab.icon} mr-2`}></i>{tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-slate-950/50 border-slate-700/50 border border-gray-700 rounded-lg p-4 sm:p-6 shadow-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {gateways.filter(g => g.provider_type === activeTab).map((gateway) => {
              const globalIndex = gateways.findIndex(g => g.provider_name === gateway.provider_name);
              return (
                <div key={gateway.provider_name} className="p-4 bg-slate-900/60 rounded border border-gray-700">
                  <div className="flex justify-between items-center mb-4 border-b border-gray-700 pb-3">
                    <h3 className="text-lg font-bold text-white uppercase">
                      {gateway.provider_name}
                    </h3>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={gateway.is_active}
                        onChange={() => toggleActive(globalIndex)}
                        className="rounded border-gray-600 bg-gray-700 accent-indigo-500 w-4 h-4"
                      />
                      <label className="text-sm font-bold text-gray-300">Active</label>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {renderCredentialsInputs(gateway, globalIndex)}
                    
                    <button
                      onClick={() => updateGateway(globalIndex)}
                      disabled={loading}
                      className="w-full mt-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded transition-colors"
                    >
                      {loading ? 'Saving...' : `Save ${gateway.provider_name} Credentials`}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GatewayConfigs;
